import React, { useMemo, useState } from 'react';
import { BarChart3, Users, MousePointerClick, Calendar as CalendarIcon, TrendingUp, ArrowUpRight, MessageCircle } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { getPageViewsInRange, getPageViews, PageViewEvent } from '../utils/analytics';
type Preset = 'hoy' | '7dias' | '30dias' | 'todo' | 'custom';
export function Control() {
  const [preset, setPreset] = useState<Preset>('7dias');
  // Default to last 7 days
  const [startDate, setStartDate] = useState<string>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });
  const handlePresetChange = (newPreset: Preset) => {
    setPreset(newPreset);
    const end = new Date();
    const start = new Date();
    switch (newPreset) {
      case 'hoy':
        // start is already today
        break;
      case '7dias':
        start.setDate(start.getDate() - 6);
        break;
      case '30dias':
        start.setDate(start.getDate() - 29);
        break;
      case 'todo':
        // Just set to a very old date
        start.setFullYear(2020);
        break;
      case 'custom':
        return;
      // Don't change dates, just the preset
    }
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };
  // Fetch and process data
  const {
    kpis,
    dailyData
  } = useMemo(() => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    // If 'todo', just get all views, otherwise filter
    const events = preset === 'todo' ? getPageViews() : getPageViewsInRange(start, end);
    let registroCount = 0;
    let graciasCount = 0;
    let whatsappCount = 0;
    // Group by date for the chart and table
    const groupedByDate: Record<string, {
      date: string;
      registro: number;
      gracias: number;
      whatsapp: number;
    }> = {};
    events.forEach(event => {
      const dateStr = event.timestamp.split('T')[0];
      if (!groupedByDate[dateStr]) {
        groupedByDate[dateStr] = {
          date: dateStr,
          registro: 0,
          gracias: 0,
          whatsapp: 0
        };
      }
      if (event.page === '/registro') {
        registroCount++;
        groupedByDate[dateStr].registro++;
      } else if (event.page === '/gracias') {
        graciasCount++;
        groupedByDate[dateStr].gracias++;
      } else if (event.page === 'whatsapp_click') {
        whatsappCount++;
        groupedByDate[dateStr].whatsapp++;
      }
    });
    // Convert grouped object to array and sort by date (newest first for table, oldest first for chart)
    const dailyArray = Object.values(groupedByDate).sort((a, b) => a.date.localeCompare(b.date));
    const ctr = registroCount > 0 ? (graciasCount / registroCount * 100).toFixed(1) : '0.0';
    const whatsappCtr = graciasCount > 0 ? (whatsappCount / graciasCount * 100).toFixed(1) : '0.0';
    return {
      kpis: {
        registro: registroCount,
        gracias: graciasCount,
        whatsapp: whatsappCount,
        ctr: ctr,
        whatsappCtr: whatsappCtr
      },
      dailyData: dailyArray
    };
  }, [startDate, endDate, preset]);
  // Reverse for table (newest first)
  const tableData = [...dailyData].reverse();
  return <div className="min-h-screen bg-gray-50 font-sans text-dark flex flex-col md:flex-row" data-id="element-345" data-mp="y62xbeseveo4">
      {/* SIDEBAR */}
      <aside className="w-full md:w-64 bg-dark text-white p-6 flex-shrink-0" data-id="element-346" data-mp="t43t62oukdq4">
        <div className="flex items-center gap-3 mb-10" data-id="element-347" data-mp="o5k804i2v5yt">
          <div className="w-10 h-10 bg-gold-metallic rounded-lg flex items-center justify-center" data-id="element-348" data-mp="3dvw7rrgi970q">
            <BarChart3 className="w-6 h-6 text-dark" data-id="element-349" data-mp="38wt0s1gysqq4" />
          </div>
          <div data-id="element-350" data-mp="38vhx01niriz0">
            <h1 className="font-bold text-xl leading-tight" data-id="element-351" data-mp="38z5pqyl541co">
              Panel de Control
            </h1>
            <p className="text-xs text-gray-400" data-id="element-352" data-mp="16cchc65d2m82">Webinar Analytics</p>
          </div>
        </div>

        <nav className="space-y-2" data-id="element-353" data-mp="rngcsj2ijrc7">
          <a href="#" className="flex items-center gap-3 px-4 py-3 bg-white/10 rounded-lg text-gold-light font-medium transition-colors" data-id="element-354" data-mp="rohnunjj4dtx">
            <TrendingUp className="w-5 h-5" data-id="element-355" data-mp="1kcdjrq19y1pn" />
            Métricas
          </a>
          <a href="/registro" target="_blank" className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 rounded-lg text-gray-300 transition-colors" data-id="element-356" data-mp="1ka9bkvwcegjk">
            <ArrowUpRight className="w-5 h-5" data-id="element-357" data-mp="1fd0ukymxkijw" />
            Ver Landing
          </a>
        </nav>
      </aside>

      {/* MAIN CONTENT */}
      <main className="flex-1 p-4 md:p-8 overflow-y-auto" data-id="element-358" data-mp="3v33vt13w8m39">
        <div className="max-w-6xl mx-auto space-y-8" data-id="element-359" data-mp="3xuc1r6ht423">
          {/* HEADER & FILTERS */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 bg-white p-4 md:p-6 rounded-2xl shadow-sm border border-gray-100" data-id="element-360" data-mp="3vhmaruhtpeh">
            <div data-id="element-361" data-mp="2d5qpmb34bp5v">
              <h2 className="text-2xl font-bold text-dark mb-1" data-id="element-362" data-mp="2d6aa9zk5krnl">
                Rendimiento del Embudo
              </h2>
              <p className="text-gray-500 text-sm" data-id="element-363" data-mp="2r3ytz42hhsr2">
                Visualiza el tráfico y conversión de tus páginas.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4" data-id="element-364" data-mp="1zfx2bx79hcy9">
              <div className="flex bg-gray-100 p-1 rounded-lg" data-id="element-365" data-mp="1uh3o22k5hhx1">
                <button onClick={() => handlePresetChange('hoy')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${preset === 'hoy' ? 'bg-white shadow-sm text-dark' : 'text-gray-600 hover:text-dark'}`} data-id="element-366" data-mp="287fjaac17wse">
                  Hoy
                </button>
                <button onClick={() => handlePresetChange('7dias')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${preset === '7dias' ? 'bg-white shadow-sm text-dark' : 'text-gray-600 hover:text-dark'}`} data-id="element-367" data-mp="1bqceazw93pi4">
                  7 Días
                </button>
                <button onClick={() => handlePresetChange('30dias')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${preset === '30dias' ? 'bg-white shadow-sm text-dark' : 'text-gray-600 hover:text-dark'}`} data-id="element-368" data-mp="3kkw1mdpdodmj">
                  30 Días
                </button>
                <button onClick={() => handlePresetChange('todo')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${preset === 'todo' ? 'bg-white shadow-sm text-dark' : 'text-gray-600 hover:text-dark'}`} data-id="element-369" data-mp="31tw7yz6xufmx">
                  Todo
                </button>
              </div>

              <div className="flex items-center gap-2" data-id="element-370" data-mp="m0xcpjh907ke">
                <div className="relative" data-id="element-371" data-mp="26l0zsjlz23qe">
                  <CalendarIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-372" data-mp="26jpwsm0cp00c" />
                  <input type="date" value={startDate} onChange={e => {
                  setStartDate(e.target.value);
                  setPreset('custom');
                }} className="pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-gold-metallic focus:border-gold-metallic outline-none" data-id="element-373" data-mp="21nj0zm1o54w4" />
                </div>
                <span className="text-gray-400" data-id="element-374" data-mp="1dwn1s3uvv7eu">-</span>
                <div className="relative" data-id="element-375" data-mp="18wqzqa5qxq1x">
                  <CalendarIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-376" data-mp="18y2c2yd9c64n" />
                  <input type="date" value={endDate} onChange={e => {
                  setEndDate(e.target.value);
                  setPreset('custom');
                }} className="pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-gold-metallic focus:border-gold-metallic outline-none" data-id="element-377" data-mp="389cwuowzmmnt" />
                </div>
              </div>
            </div>
          </div>

          {/* KPI CARDS */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6" data-id="element-378" data-mp="1tey3gwqj6woc">
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4" data-id="element-379" data-mp="1th103zm6vo21">
              <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-380" data-mp="1jgs34jkdhyll">
                <Users className="w-6 h-6" data-id="element-381" data-mp="1of1xwt26qx6o" />
              </div>
              <div data-id="element-382" data-mp="1ohofzmmpcv35">
                <p className="text-sm font-medium text-gray-500 mb-1" data-id="element-383" data-mp="3dv1f8xfyjsxe">
                  Visitas Registro
                </p>
                <p className="text-3xl font-bold text-dark" data-id="element-384" data-mp="3isti2mi54zgd">{kpis.registro}</p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4" data-id="element-385" data-mp="1772er1see682">
              <div className="w-12 h-12 bg-green-50 text-green-600 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-386" data-mp="12998k8r3hc7s">
                <MousePointerClick className="w-6 h-6" data-id="element-387" data-mp="1c5m47c67a056" />
              </div>
              <div data-id="element-388" data-mp="1c3j2vvzlkkl5">
                <p className="text-sm font-medium text-gray-500 mb-1" data-id="element-389" data-mp="1px0mfgsa9xb6">
                  Visitas Gracias
                </p>
                <p className="text-3xl font-bold text-dark" data-id="element-390" data-mp="1lvynz266xpa9">{kpis.gracias}</p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4 relative overflow-hidden" data-id="element-391" data-mp="1zu5c3qlc8awq">
              <div className="absolute right-0 top-0 w-24 h-24 bg-gold-metallic/10 rounded-bl-full -mr-4 -mt-4" data-id="element-392" data-mp="1uu0qxn9his06"></div>
              <div className="w-12 h-12 bg-gold-light text-gold-dark rounded-xl flex items-center justify-center flex-shrink-0 relative z-10" data-id="element-393" data-mp="i2jnquqk66x">
                <TrendingUp className="w-6 h-6" data-id="element-394" data-mp="3gil1zrai0i0t" />
              </div>
              <div className="relative z-10" data-id="element-395" data-mp="3biqb3nqq3sfr">
                <p className="text-sm font-medium text-gray-500 mb-1" data-id="element-396" data-mp="3bgvnu2rzbo8i">
                  CTR Registro→Gracias
                </p>
                <p className="text-3xl font-bold text-dark" data-id="element-397" data-mp="36l6sycqwmvij">{kpis.ctr}%</p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4" data-id="element-398" data-mp="32dkgeczgwv8">
              <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0" data-id="element-399" data-mp="3u8g2s1z6vy3g">
                <MessageCircle className="w-6 h-6" data-id="element-400" data-mp="3pgwwi4ehjzyj" />
              </div>
              <div data-id="element-401" data-mp="3lhfrtd6vv6uf">
                <p className="text-sm font-medium text-gray-500 mb-1" data-id="element-402" data-mp="3lfuw3k8u1huz">
                  Clics WhatsApp
                </p>
                <p className="text-3xl font-bold text-dark" data-id="element-403" data-mp="lufu35r7jcvz">{kpis.whatsapp}</p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 flex items-center gap-4 relative overflow-hidden" data-id="element-404" data-mp="h0las3fikzxf">
              <div className="absolute right-0 top-0 w-24 h-24 bg-emerald-500/10 rounded-bl-full -mr-4 -mt-4" data-id="element-405" data-mp="24nhcfoyxe56h"></div>
              <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center flex-shrink-0 relative z-10" data-id="element-406" data-mp="28p2vxt5ythpn">
                <TrendingUp className="w-6 h-6" data-id="element-407" data-mp="2dmkxuzlkewo5" />
              </div>
              <div className="relative z-10" data-id="element-408" data-mp="2inim9tx64my3">
                <p className="text-sm font-medium text-gray-500 mb-1" data-id="element-409" data-mp="2nmvbumffc84d">
                  CTR Gracias→WhatsApp
                </p>
                <p className="text-3xl font-bold text-dark" data-id="element-410" data-mp="2sewinsttp6n0">
                  {kpis.whatsappCtr}%
                </p>
              </div>
            </div>
          </div>

          {/* CHART */}
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100" data-id="element-411" data-mp="31fkxgpr1jb0y">
            <h3 className="text-lg font-bold text-dark mb-6" data-id="element-412" data-mp="36ctgstnxdl3s">
              Tráfico a lo largo del tiempo
            </h3>
            <div className="h-[300px] w-full" data-id="element-413" data-mp="16c52iyz1j7ka">
              {dailyData.length > 0 ? <ResponsiveContainer width="100%" height="100%" data-id="element-414" data-mp="11dbkqucq0ueb">
                  <LineChart data={dailyData} margin={{
                top: 5,
                right: 20,
                bottom: 5,
                left: 0
              }} data-id="element-415" data-mp="1g0womqhf6xna">
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" data-id="element-416" data-mp="1p3xucq2dimqf" />
                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{
                  fill: '#6b7280',
                  fontSize: 12
                }} dy={10} data-id="element-417" data-mp="1yzipq5cxsor7" />
                    <YAxis axisLine={false} tickLine={false} tick={{
                  fill: '#6b7280',
                  fontSize: 12
                }} data-id="element-418" data-mp="3vuf2zq8hy05f" />
                    <Tooltip contentStyle={{
                  borderRadius: '12px',
                  border: 'none',
                  boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                }} data-id="element-419" data-mp="21jp4z97041n2" />
                    <Legend iconType="circle" wrapperStyle={{
                  paddingTop: '20px'
                }} data-id="element-420" data-mp="1xjeov3phvuuh" />
                    <Line type="monotone" name="Registro" dataKey="registro" stroke="#1f2937" strokeWidth={3} dot={{
                  r: 4,
                  strokeWidth: 2
                }} activeDot={{
                  r: 6
                }} data-id="element-421" data-mp="1nk5xmim0kubt" />
                    <Line type="monotone" name="Gracias" dataKey="gracias" stroke="#D4AF37" strokeWidth={3} dot={{
                  r: 4,
                  strokeWidth: 2
                }} activeDot={{
                  r: 6
                }} data-id="element-422" data-mp="1dvnczt7mo69c" />
                    <Line type="monotone" name="WhatsApp" dataKey="whatsapp" stroke="#25D366" strokeWidth={3} dot={{
                  r: 4,
                  strokeWidth: 2
                }} activeDot={{
                  r: 6
                }} data-id="element-423" data-mp="q4i1fq8q004u" />
                  </LineChart>
                </ResponsiveContainer> : <div className="w-full h-full flex items-center justify-center text-gray-400" data-id="element-424" data-mp="61c43579b0lb">
                  No hay datos en este rango de fechas
                </div>}
            </div>
          </div>

          {/* TABLE */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-425" data-mp="3ejiaqkhi1sqi">
            <div className="p-6 border-b border-gray-100" data-id="element-426" data-mp="3jhjt1373po0x">
              <h3 className="text-lg font-bold text-dark" data-id="element-427" data-mp="3jfyxpbcyq120">Desglose Diario</h3>
            </div>
            <div className="overflow-x-auto" data-id="element-428" data-mp="3ofv39f1b60jo">
              <table className="w-full text-left border-collapse" data-id="element-429" data-mp="3t8eh8d3wqxp0">
                <thead data-id="element-430" data-mp="3t9pl0cxcs5g4">
                  <tr className="bg-gray-50 text-gray-500 text-sm" data-id="element-431" data-mp="2vt0kqno1xb8u">
                    <th className="px-6 py-4 font-medium" data-id="element-432" data-mp="2vqo5ii9ohezh">Fecha</th>
                    <th className="px-6 py-4 font-medium" data-id="element-433" data-mp="30qag0jy4gh4l">Registro</th>
                    <th className="px-6 py-4 font-medium" data-id="element-434" data-mp="2sq47588y7fdr">Gracias</th>
                    <th className="px-6 py-4 font-medium" data-id="element-435" data-mp="2nrsxoymkf6w2">CTR %</th>
                    <th className="px-6 py-4 font-medium" data-id="element-436" data-mp="2nrj4z3a4ylmo">WhatsApp</th>
                    <th className="px-6 py-4 font-medium" data-id="element-437" data-mp="32fnp8bou757k">CTR WA %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-438" data-mp="2xi4dbifm34is">
                  {tableData.length > 0 ? tableData.map(row => {
                  const rowCtr = row.registro > 0 ? (row.gracias / row.registro * 100).toFixed(1) : '0.0';
                  const rowWaCtr = row.gracias > 0 ? (row.whatsapp / row.gracias * 100).toFixed(1) : '0.0';
                  return <tr key={row.date} className="hover:bg-gray-50/50 transition-colors" data-id="element-439" data-mp="3q6t68u6ik3jg">
                          <td className="px-6 py-4 text-sm font-medium text-dark" data-id="element-440" data-mp="1mraun0muat8">
                            {row.date}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600" data-id="element-441" data-mp="3oqfnks86k8s4">
                            {row.registro}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600" data-id="element-442" data-mp="kawc9ntthjov">
                            {row.gracias}
                          </td>
                          <td className="px-6 py-4 text-sm" data-id="element-443" data-mp="p8o46hugp5t2">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${Number(rowCtr) > 20 ? 'bg-green-100 text-green-700' : Number(rowCtr) > 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-444" data-mp="biuczsvhi9g6">
                              {rowCtr}%
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600" data-id="element-445" data-mp="ja2uclx46hj3">
                            {row.whatsapp}
                          </td>
                          <td className="px-6 py-4 text-sm" data-id="element-446" data-mp="eh1by3xp7zur">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${Number(rowWaCtr) > 20 ? 'bg-emerald-100 text-emerald-700' : Number(rowWaCtr) > 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-447" data-mp="9l2tewz1xc29">
                              {rowWaCtr}%
                            </span>
                          </td>
                        </tr>;
                }) : <tr data-id="element-448" data-mp="nawm5qdoggh9">
                      <td colSpan={6} className="px-6 py-8 text-center text-gray-400 text-sm" data-id="element-449" data-mp="ndsemz6racyb">
                        No hay datos para mostrar
                      </td>
                    </tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    </div>;
}